import { InformationModal, DefaultPrimaryButton } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useState, useMemo } from "react";
import { useGetFlaggedParts, useUnFlagParts } from "@/hooks/flaggedPartHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useGetReasonPriceChange } from "@/hooks/masterHook";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useUserInfo } from "@/context/UserInfoContext";
import UnFlaggedWarningMessage from "./UnFlaggedWarningMessage";

interface UnFlaggedModalProps {
  open: boolean;
  handleClose: () => void;
  selectedParts: Array<{
    partId: number;
    approvalId?: string;
  }>;
  clear?: () => void;
  requiredApprovalId?: boolean;
}

const UnFlaggedModal = ({
  open,
  handleClose,
  selectedParts,
  clear,
  requiredApprovalId = false,
}: UnFlaggedModalProps) => {
  const userInfo = useUserInfo();
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedReason, setSelectedReason] = useState<number>(0);

  const {
    data: flaggedParts,
    isFetching: isFetchingFlaggedParts,
    isError: isErrorFlaggedParts,
  } = useGetFlaggedParts(
    {
      part_id:
        selectedParts?.length === 1 ? selectedParts[0]?.partId : undefined,
    },
    requiredApprovalId &&
      open &&
      !!selectedParts &&
      selectedParts?.length === 1,
  );
  const { mutate: unFlagParts, isPending: isUnFlagging } = useUnFlagParts();
  const {
    data: reasonPriceChange,
    isFetching: isFetchingReasonPriceChange,
    isError: isErrorReasonPriceChange,
  } = useGetReasonPriceChange({ active_only: true }, true);

  useQueryHandler({
    isFetching:
      isUnFlagging || isFetchingReasonPriceChange || isFetchingFlaggedParts,
    isError: isErrorReasonPriceChange || isErrorFlaggedParts,
  });

  const isNoApproval = useMemo(() => {
    if (flaggedParts && flaggedParts?.data?.length > 0) {
      return flaggedParts.data.every((part) => {
        const approvalIds = part.approval_id?.split(",").map((id) => id.trim());
        return (
          !approvalIds ||
          approvalIds.length === 0 ||
          approvalIds.every((id) => id === "")
        );
      });
    }
    return selectedParts?.every((part) => {
      const approvalIds = part.approvalId?.split(",").map((id) => id.trim());
      return (
        !approvalIds ||
        approvalIds.length === 0 ||
        approvalIds.every((id) => id === "")
      );
    });
  }, [flaggedParts, selectedParts]);

  const { isYoursApproval, isAdminFlagged } = useMemo(() => {
    let isYours = true;
    let isAdmin = false;
    if (!userInfo) return { isYoursApproval: false, isAdminFlagged: false };

    isAdmin = userInfo.groups?.some((g) => g.trim().toLowerCase() === "pu admin") || false;
    const checkApproval : string = isAdmin ? "ADMIN" : userInfo.user_id;

    if (flaggedParts && flaggedParts?.data?.length > 0) {
      flaggedParts.data?.forEach((part) => {
        if (
          part.approval_id &&
          !part.approval_id
            .split(",")
            .map((id) => id.trim())
            .includes(checkApproval)
        ) {
          isYours = false;
        }
      });
    } else {
      selectedParts?.forEach((part) => {
        if (
          part.approvalId &&
          !part.approvalId
            .split(",")
            .map((id) => id.trim())
            .includes(checkApproval)
        ) {
          isYours = false;
        }
      });
    }

    return { isYoursApproval: isYours, isAdminFlagged: isAdmin };
  }, [flaggedParts, selectedParts, userInfo]);

  const approvalIdAlertText = useMemo(() => {
    let approvalIds = new Set<string | undefined>();
    if (flaggedParts && flaggedParts?.data?.length > 0) {
      flaggedParts.data?.forEach((part) => {
        part.approval_id
          ?.split(",")
          .map((id) => id.trim())
          .forEach((id) => {
            if (id !== userInfo?.user_id) {
              approvalIds.add(id);
            }
          });
      });
      return `(Approval Id: ${[...approvalIds].join(", ")})`;
    }
    if (selectedParts && selectedParts.length > 0) {
      approvalIds = new Set(
        selectedParts
          .map((part) => part.approvalId?.split(",").map((id) => id.trim()))
          .flat()
          .filter((id) => id !== userInfo?.user_id),
      );
    }
    return `(Approval Id: ${[...approvalIds].join(", ")})`;
  }, [flaggedParts, selectedParts, userInfo?.user_id]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedReason) {
      newErrors.selectedReason = "กรุณาเลือกเหตุผล";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUnFlagged = () => {
    if (!validateForm()) return;
    unFlagParts(
      {
        partIds: selectedParts.map((part) => part.partId),
        reasonId: Number(selectedReason),
        isAdminFlagged: isAdminFlagged,
      },
      {
        onSuccess: () => {
          showToast("ปลด Flag สำเร็จ", "success");
          if (clear) {
            clear();
          }
        },
        onError: (error) => {
          console.error("Error unflagging parts:", error);
          ErrorAlert(
            "Error",
            getAxiosErrorMessage(error) || "เกิดข้อผิดพลาดในการปลด Flag",
          );
        },
      },
    );
    onClose();
  };

  const onClose = () => {
    setSelectedReason(0);
    setErrors({});
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">Reason of Price Change</Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <FormControl size="small" sx={{ width: "100%", mt: 2, mb: 1 }}>
            <Select
              error={!!errors.selectedReason}
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {(isAdminFlagged
                ? reasonPriceChange
                : reasonPriceChange?.filter((r) => !r.is_admin_reason)
              )?.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.name}
                </MenuItem>
              ))}
            </Select>

            {errors.selectedReason && (
              <FormHelperText sx={{ color: "error.main", fontSize: "0.8rem" }}>
                {errors.selectedReason}
              </FormHelperText>
            )}
          </FormControl>
          <UnFlaggedWarningMessage
            isNoApproval={isNoApproval}
            isYoursApproval={isYoursApproval}
            approvalIdAlertText={approvalIdAlertText}
          />
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={handleUnFlagged}
            disabled={!isYoursApproval || isNoApproval}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UnFlaggedModal;
